import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/comics';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/comics', label: '漫画大全' },
  { to: '/about', label: '关于我们' },
  { to: '/contact', label: '联系我们' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const catRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/comics?q=${encodeURIComponent(q)}` : '/comics');
    setKeyword('');
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 md:px-6" aria-label="主导航">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-magic-line text-xl" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl text-primary-500">麻瓜漫画</span>
              <span className="text-[10px] tracking-[0.3em] text-foreground-500 mt-1">MUGUA COMIC</span>
            </span>
          </Link>

          {/* desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {/* category dropdown */}
            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 whitespace-nowrap"
                aria-haspopup="true"
                aria-expanded={catOpen}
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <div className="absolute right-0 top-full mt-3 w-72 rounded-xl bg-background-200 p-2 shadow-lg border border-background-300">
                  <p className="px-3 py-2 text-xs text-foreground-500">选择分类浏览</p>
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/comics?category=${c.id}`}
                        onClick={() => setCatOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-primary-500/15 hover:text-primary-400 whitespace-nowrap"
                      >
                        <i className={`${c.icon} text-primary-500`}></i>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* search + actions */}
          <div className="hidden lg:flex items-center gap-3">
            <form onSubmit={onSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 作者"
                className="h-10 w-56 rounded-full bg-background-200/70 pl-9 pr-16 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-primary-400/60 focus:ring-2"
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <Link
              to="/comics"
              className="h-10 flex items-center gap-2 rounded-full bg-background-200/70 px-4 text-sm text-foreground-700 hover:text-primary-500 whitespace-nowrap"
            >
              <i className="ri-book-2-line"></i>
              我的书架
            </Link>
          </div>

          {/* mobile toggle */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-full text-foreground-950"
            aria-label="切换菜单"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur-md">
          <div className="px-4 py-4 flex flex-col gap-1">
            <form onSubmit={onSearch} className="relative mb-2">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 作者"
                className="h-11 w-full rounded-full bg-background-200/70 pl-9 pr-14 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-primary-400/60 focus:ring-2"
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-9 px-3 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-sm font-medium ${isActive ? 'bg-primary-500/15 text-primary-500' : 'text-foreground-800'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <p className="px-4 pt-3 pb-1 text-xs text-foreground-500">分类浏览</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.slice(0, 8).map((c) => (
                <Link
                  key={c.id}
                  to={`/comics?category=${c.id}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 whitespace-nowrap"
                >
                  <i className={`${c.icon} text-primary-500`}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}